'use client';

/**
 * Comments bridge — carries canvas-comment state ACROSS the
 * `CanvasYjsProvider` boundary (the Y.Doc lives inside the provider,
 * which wraps only the canvas; the Collaborate panel renders outside).
 * Same module-store pattern as `presence-bridge` / `panel-events`:
 *   - the canvas-side `<CanvasComments>` mirrors the thread list out +
 *     publishes a count, and listens for place / open commands,
 *   - the Collaborate panel reads the list and fires the commands.
 */

import { useSyncExternalStore } from 'react';

/** Flat thread summary for the Collaborate panel's comments list. */
export interface CommentThreadSummary {
  id: string;
  authorName: string;
  resolved: boolean;
  createdAt: number;
  /** first message text, for the row preview. */
  preview: string;
  count: number;
}

/* ---- thread mirror (canvas → panel) ------------------------------- */

let threads: CommentThreadSummary[] = [];
const threadSubs = new Set<() => void>();

export function publishCommentThreads(next: CommentThreadSummary[]): void {
  threads = next;
  threadSubs.forEach((fn) => fn());
}

export function useCommentThreadsMirror(): CommentThreadSummary[] {
  return useSyncExternalStore(
    (cb) => {
      threadSubs.add(cb);
      return () => threadSubs.delete(cb);
    },
    () => threads,
    () => threads,
  );
}

/** Count of OPEN (unresolved) threads — for a Collaborate-section badge. */
export function useOpenCommentCount(): number {
  return useCommentThreadsMirror().filter((t) => !t.resolved).length;
}

/* ---- place-comment command (panel → canvas) ----------------------- */

const placeSubs = new Set<() => void>();

/** Panel "Add comment" → arm canvas placement mode (next click drops a pin). */
export function requestPlaceComment(): void {
  placeSubs.forEach((fn) => fn());
}

export function subscribePlaceComment(cb: () => void): () => void {
  placeSubs.add(cb);
  return () => placeSubs.delete(cb);
}

// Direct placement at a specific canvas point — the canvas right-click
// "Comment here" already knows where the cursor was, so it skips the
// arm-then-click step and opens the composer there immediately.
const placeAtSubs = new Set<(x: number, y: number) => void>();

export function requestNewThreadAt(x: number, y: number): void {
  placeAtSubs.forEach((fn) => fn(x, y));
}

export function subscribeNewThreadAt(
  cb: (x: number, y: number) => void,
): () => void {
  placeAtSubs.add(cb);
  return () => placeAtSubs.delete(cb);
}

/* ---- open-thread command (panel → canvas) ------------------------- */

const openSubs = new Set<(threadId: string) => void>();

/** Panel thread-row click → open that thread on the canvas + pan to it. */
export function requestOpenThread(threadId: string): void {
  openSubs.forEach((fn) => fn(threadId));
}

export function subscribeOpenThread(
  cb: (threadId: string) => void,
): () => void {
  openSubs.add(cb);
  return () => openSubs.delete(cb);
}
